/**
 * Resolved user profile (name parts + email) sourced from the RUM user-details
 * endpoint (`/v1/users/{oid}`), NOT from the MSAL account.
 *
 * WHY: external / B2B-guest users have an empty MSAL `account.name`, so deriving
 * the display name and avatar initials from `account.name` (which falls back to
 * the email `account.username`) renders the raw email in the avatar. The legacy
 * shell (`raptor-shell-ui` `AuthorizeService.mapUserDetails`) instead reads
 * `firstName` / `lastName` / `name` from the RUM profile and builds the avatar
 * as `firstName[0] + lastName[0]`. This holder mirrors that: the RUM profile the
 * shell already fetches (see `legacy-user-bridge.ts`) is captured here so the
 * header avatar and the legacy `currentUser` blob show the real name.
 */

export interface UserProfile {
  firstName: string;
  lastName: string;
  name: string;
  email: string;
  /** RUM `userRoleTypeId`; 6 or 7 => global user (legacy `mapUserDetails`). */
  userRoleTypeId: number | null;
  /** Server-side last-selected client GUID (legacy `currentUser.recentClient`). */
  recentClient: string;
  /** Legacy `currentUser.lastAcceptedPrivacyDisclaimer`. */
  lastAcceptedPrivacyDisclaimer: string;
}

const listeners = new Set<() => void>();
let currentProfile: UserProfile | null = null;

function notify(): void {
  for (const listener of listeners) listener();
}

export function getUserProfile(): UserProfile | null {
  return currentProfile;
}

/**
 * Capture the name parts from a RUM user-details response. Returns the resolved
 * profile, or `null` when the payload carries no usable identity fields.
 */
export function setUserProfileFromRum(profile: unknown): UserProfile | null {
  if (!profile || typeof profile !== 'object') return null;
  const record = profile as Record<string, unknown>;
  const str = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');
  const num = (value: unknown): number | null =>
    typeof value === 'number' && Number.isFinite(value) ? value : null;

  const next: UserProfile = {
    firstName: str(record.firstName),
    lastName: str(record.lastName),
    name: str(record.name),
    email: str(record.email),
    userRoleTypeId: num(record.userRoleTypeId),
    recentClient: str(record.recentClient),
    lastAcceptedPrivacyDisclaimer: str(record.lastAcceptedPrivacyDisclaimer),
  };

  if (!next.firstName && !next.lastName && !next.name && !next.email) return null;
  if (
    currentProfile &&
    currentProfile.firstName === next.firstName &&
    currentProfile.lastName === next.lastName &&
    currentProfile.name === next.name &&
    currentProfile.email === next.email &&
    currentProfile.userRoleTypeId === next.userRoleTypeId &&
    currentProfile.recentClient === next.recentClient &&
    currentProfile.lastAcceptedPrivacyDisclaimer === next.lastAcceptedPrivacyDisclaimer
  ) {
    return currentProfile;
  }

  currentProfile = next;
  notify();
  return next;
}

export function clearUserProfile(): void {
  if (currentProfile === null) return;
  currentProfile = null;
  notify();
}

export function subscribeUserProfile(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
